import {
  Component,
  ElementRef,
  HostListener,
  Injector,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  afterNextRender,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  EXAM_DURATION_SECONDS,
  OptionView,
  Question,
  Quiz,
  QuizBank,
  QuizMode,
  DEFAULT_BANK,
  bankFromCategory,
  minPassingScore,
  quizListPath,
  toOptionView,
} from '../../../models/quiz';
import { celebrate, resetConfetti } from '../../../motion/celebrate';
import { gsap } from '../../../motion/gsap';
import { isMobileViewport, prefersReducedMotion } from '../../../motion/motion-env';
import { MotionService } from '../../../motion/motion.service';
import { AnalyticsService, TestDetails } from '../../../services/analytics.service';
import { QuizDataService } from '../../../services/quiz-data.service';
import { QuestionLanguageService } from '../../../services/question-language.service';
import { QuizResult, QuizResultsService } from '../../../services/quiz-results.service';

const SWIPE_MIN_DISTANCE = 60;
const TIME_WARNING_SECONDS = 5 * 60;
const TIME_DANGER_SECONDS = 60;
/** Starts after the result modal's .2s pop. */
const CELEBRATE_DELAY_MS = 180;

@Component({
  selector: 'app-quiz',
  templateUrl: './quiz.component.html',
  styleUrls: ['./quiz.component.css'],
})
export class QuizComponent implements OnInit, OnDestroy {
  @ViewChild('questionCard') questionCard?: ElementRef<HTMLElement>;

  quiz?: Quiz;
  questions: Question[] = [];
  optionViews: OptionView[][] = [];
  answers: (number | null)[] = [];
  currentIndex = 0;
  mode: QuizMode = 'exam';
  bank: QuizBank = DEFAULT_BANK;
  currentLang = 'am';
  /** Language of the questions; differs from currentLang when falling back to Armenian. */
  questionsLang = 'am';
  isFallback = false;
  loading = true;
  notFound = false;

  timeLeft = EXAM_DURATION_SECONDS;
  showFinishConfirm = false;
  result: QuizResult | null = null;
  /** The current question was answered during this visit: its practice feedback animates once. */
  justAnswered = false;

  private readonly zone = inject(NgZone);
  private readonly injector = inject(Injector);
  private readonly motion = inject(MotionService);
  private readonly analytics = inject(AnalyticsService);
  private swapTween?: gsap.core.Tween;
  private celebrateTimer?: ReturnType<typeof setTimeout>;

  private deadline: number | null = null;
  private startedAt = Date.now();
  private timerId?: ReturnType<typeof setInterval>;
  private touchStart: { x: number; y: number } | null = null;
  private subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private quizData: QuizDataService,
    private quizResults: QuizResultsService,
    private questionLanguage: QuestionLanguageService
  ) {}

  ngOnInit() {
    this.subscription.add(
      combineLatest([this.route.paramMap, this.route.queryParamMap])
        .pipe(
          switchMap(([params, query]) => {
            this.stopTimer();
            this.loading = true;
            this.currentLang = params.get('lang') || storedLanguage();
            this.translate.use(this.currentLang);
            this.mode = query.get('mode') === 'practice' ? 'practice' : 'exam';
            const bank = bankFromCategory(params.get('category'));
            if (!bank) {
              return of({ quiz: undefined, set: { quizzes: [], language: this.currentLang, isFallback: false } });
            }
            this.bank = bank;
            const questionsLang = this.questionLanguage.get(this.currentLang);
            return this.quizData.getQuiz(questionsLang, Number(params.get('id')), bank);
          })
        )
        .subscribe(({ quiz, set }) => {
          this.loading = false;
          this.isFallback = set.isFallback;
          this.questionsLang = set.language;
          this.notFound = !quiz;
          if (quiz) {
            this.start(quiz);
          }
        })
    );
  }

  ngOnDestroy() {
    this.stopTimer();
    this.subscription.unsubscribe();
    this.swapTween?.kill();
    this.stopCelebration();
  }

  // ---------------------------------------------------------------- state

  get currentQuestion(): Question | undefined {
    return this.questions[this.currentIndex];
  }

  get currentAnswer(): number | null {
    return this.answers[this.currentIndex] ?? null;
  }

  get isLastQuestion() {
    return this.currentIndex === this.questions.length - 1;
  }

  get answeredCount() {
    return this.answers.filter((answer) => answer !== null).length;
  }

  get unansweredCount() {
    return this.questions.length - this.answeredCount;
  }

  get correctCount() {
    return this.questions.filter((_, i) => this.isCorrect(i)).length;
  }

  get progressPercent() {
    return this.questions.length ? (this.answeredCount / this.questions.length) * 100 : 0;
  }

  /** In practice mode the answer is revealed (and locked) as soon as it is chosen. */
  get showFeedback() {
    return this.mode === 'practice' && this.currentAnswer !== null;
  }

  get timerState(): 'normal' | 'warning' | 'danger' {
    if (this.timeLeft <= TIME_DANGER_SECONDS) return 'danger';
    if (this.timeLeft <= TIME_WARNING_SECONDS) return 'warning';
    return 'normal';
  }

  get formattedTime() {
    const minutes = Math.floor(this.timeLeft / 60);
    const seconds = this.timeLeft % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  /** "ABC" / "DT" for tests of the 2026 edition, shown next to the title. */
  get v2Category(): string | null {
    return this.bank.category.toUpperCase();
  }

  get listLink() {
    return quizListPath(this.currentLang, this.bank);
  }

  get resultTitle() {
    if (!this.result) return '';
    if (this.result.timeUp) return this.translate.instant('quiz.timeIsUpTitle');
    return this.translate.instant(this.result.passed ? 'quiz.passedTitle' : 'quiz.failedTitle');
  }

  get resultMessage() {
    if (!this.result) return '';
    return this.translate.instant(this.result.passed ? 'quiz.passedMessage' : 'quiz.failedMessage', {
      score: this.result.score,
      totalQuestions: this.result.totalQuestions,
      minPassingScore: this.result.minPassingScore,
    });
  }

  isCorrectOption(optionIndex: number, questionIndex = this.currentIndex) {
    return optionIndex === this.questions[questionIndex].correctAnswerIndex - 1;
  }

  isCorrect(questionIndex: number) {
    const answer = this.answers[questionIndex];
    return answer !== null && answer !== undefined && this.isCorrectOption(answer, questionIndex);
  }

  trackByIndex(index: number) {
    return index;
  }

  // -------------------------------------------------------------- actions

  selectAnswer(optionIndex: number) {
    if (!this.currentQuestion || this.result || this.showFeedback) {
      return;
    }
    if (optionIndex < 0 || optionIndex >= this.currentQuestion.options.length) {
      return;
    }
    this.answers[this.currentIndex] = optionIndex;
    this.justAnswered = true;
    this.saveProgress();
  }

  /**
   * A mouse or touch answer must not leave focus on the option: it would follow to the next
   * question, where Enter/Space would answer it instead of moving on. Keyboard activation
   * (click.detail === 0) keeps focus for keyboard users.
   */
  releasePointerFocus(event: MouseEvent) {
    if (event.detail > 0) (event.currentTarget as HTMLElement | null)?.blur();
  }

  goTo(index: number) {
    if (index < 0 || index >= this.questions.length || index === this.currentIndex) {
      return;
    }
    const dir = Math.sign(index - this.currentIndex);
    const focusedOption = this.focusedOptionIndex();
    this.currentIndex = index;
    this.justAnswered = false;
    this.saveProgress();
    this.bringQuestionIntoView();
    this.preloadNextImage();
    afterNextRender(
      () =>
        this.motion.outside(() => {
          this.restoreOptionFocus(focusedOption);
          this.swapIn(dir);
        }),
      { injector: this.injector }
    );
  }

  next() {
    if (this.isLastQuestion) {
      this.requestFinish();
    } else {
      this.goTo(this.currentIndex + 1);
    }
  }

  previous() {
    this.goTo(this.currentIndex - 1);
  }

  /** Used from the finish confirmation to jump back to the first skipped question. */
  goToFirstUnanswered() {
    this.showFinishConfirm = false;
    const index = this.answers.findIndex((answer) => answer === null);
    if (index !== -1) {
      this.goTo(index);
    }
  }

  requestFinish() {
    if (this.unansweredCount > 0) {
      this.showFinishConfirm = true;
    } else {
      this.finish(false);
    }
  }

  finish(timeUp: boolean) {
    if (!this.quiz || this.result) {
      return;
    }
    this.stopTimer();
    this.showFinishConfirm = false;

    const score = this.correctCount;
    const passingScore = minPassingScore(this.questions.length);
    this.result = {
      quizId: this.quiz.id,
      bank: this.bank,
      language: this.currentLang,
      mode: this.mode,
      questions: this.questions,
      answers: [...this.answers],
      score,
      totalQuestions: this.questions.length,
      minPassingScore: passingScore,
      passed: score >= passingScore,
      timeUp,
      durationSeconds: Math.round((Date.now() - this.startedAt) / 1000),
      finishedAt: Date.now(),
    };
    this.quizResults.setResult(this.result);
    this.quizResults.clearProgress(this.bank, this.questionsLang, this.quiz.id, this.mode);

    if (this.result.passed) {
      this.analytics.testPassed(this.testDetails(this.quiz));
      this.motion.outside(() => {
        this.celebrateTimer = setTimeout(() => celebrate(this.zone).catch(() => undefined), CELEBRATE_DELAY_MS);
      });
    }
  }

  showResults() {
    const target = this.currentLang === 'am' ? '/quiz-results' : `/${this.currentLang}/quiz-results`;
    this.router.navigate([target]);
  }

  restart() {
    if (!this.quiz) {
      return;
    }
    this.quizResults.clearProgress(this.bank, this.questionsLang, this.quiz.id, this.mode);
    this.stopCelebration();
    this.start(this.quiz);
  }

  // ------------------------------------------------------- input handlers

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (!this.quiz || this.result || this.showFinishConfirm) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName ?? '';
    if (/^(INPUT|SELECT|TEXTAREA)$/.test(tag)) return;

    if (/^[1-9]$/.test(event.key)) {
      this.selectAnswer(Number(event.key) - 1);
    } else if (event.key === 'ArrowRight') {
      this.next();
    } else if (event.key === 'ArrowLeft') {
      this.previous();
    } else if (event.key === 'Enter' && tag !== 'BUTTON' && tag !== 'A') {
      this.next();
    } else {
      return;
    }
    event.preventDefault();
  }

  onTouchStart(event: TouchEvent) {
    const touch = event.changedTouches[0];
    this.touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
  }

  onTouchEnd(event: TouchEvent) {
    const touch = event.changedTouches[0];
    const start = this.touchStart;
    this.touchStart = null;
    if (!touch || !start) return;

    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    // Only clearly horizontal swipes, so normal vertical scrolling is never hijacked.
    if (Math.abs(dx) < SWIPE_MIN_DISTANCE || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    if (dx < 0 && !this.isLastQuestion) {
      this.next();
    } else if (dx > 0) {
      this.previous();
    }
  }

  // -------------------------------------------------------------- helpers

  private start(quiz: Quiz) {
    this.quiz = quiz;
    this.questions = quiz.questions;
    this.optionViews = quiz.questions.map((question) => question.options.map(toOptionView));
    this.result = null;
    this.showFinishConfirm = false;
    this.justAnswered = false;

    // Answers are restored by question id, so they survive a page refresh.
    const progress = this.quizResults.getProgress(this.bank, this.questionsLang, quiz.id, this.mode);
    this.answers = this.questions.map((question) => {
      const saved = progress?.answers[question.id];
      return typeof saved === 'number' && saved < question.options.length ? saved : null;
    });
    this.currentIndex = Math.min(Math.max(progress?.currentIndex ?? 0, 0), this.questions.length - 1);
    this.startedAt = progress?.startedAt ?? Date.now();
    this.deadline =
      this.mode === 'exam' ? progress?.deadline ?? Date.now() + EXAM_DURATION_SECONDS * 1000 : null;

    this.saveProgress();
    this.startTimer();
    this.preloadNextImage();
    this.analytics.testStarted(this.testDetails(quiz));
  }

  private testDetails(quiz: Quiz): TestDetails {
    return { bank: this.bank, id: quiz.id, mode: this.mode, language: this.questionsLang };
  }

  /** Starts loading the next question's picture so it is usually ready when the user moves on. */
  private preloadNextImage() {
    const next = this.questions[this.currentIndex + 1]?.imagePath;
    if (next && typeof Image !== 'undefined') {
      new Image().src = next;
    }
  }

  private startTimer() {
    this.stopTimer();
    if (this.deadline === null) return;
    this.tick();
    if (!this.result) {
      this.timerId = setInterval(() => this.tick(), 1000);
    }
  }

  /** Time is derived from a deadline, so throttled background tabs on phones stay accurate. */
  private tick() {
    if (this.deadline === null) return;
    this.timeLeft = Math.max(0, Math.ceil((this.deadline - Date.now()) / 1000));
    if (this.timeLeft === 0) {
      this.finish(true);
    }
  }

  private stopTimer() {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = undefined;
    }
  }

  private saveProgress() {
    if (!this.quiz || this.result) return;
    const answers: Record<string, number> = {};
    this.questions.forEach((question, i) => {
      const answer = this.answers[i];
      if (answer !== null && answer !== undefined) {
        answers[question.id] = answer;
      }
    });
    this.quizResults.saveProgress(this.bank, this.questionsLang, this.quiz.id, this.mode, {
      answers,
      currentIndex: this.currentIndex,
      deadline: this.deadline,
      startedAt: this.startedAt,
    });
  }

  /**
   * The new question's content slides in from the side it comes from. Opacity, never
   * visibility, so the focused option stays focused; overwrite restarts cleanly on rapid presses.
   */
  private swapIn(dir: number) {
    const card = this.questionCard?.nativeElement;
    if (!dir || prefersReducedMotion() || !card) return;
    this.swapTween = gsap.fromTo(
      Array.from(card.children),
      { x: (isMobileViewport() ? 12 : 18) * dir, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 0.26,
        ease: 'power2.out',
        stagger: 0.03,
        overwrite: 'auto',
        clearProps: 'transform,opacity',
      }
    );
  }

  /** Index of the focused answer option, or -1. */
  private focusedOptionIndex(): number {
    const card = this.questionCard?.nativeElement;
    if (!card || typeof document === 'undefined') return -1;
    return Array.from(card.querySelectorAll('.quiz-option')).indexOf(document.activeElement as Element);
  }

  /** The next question can have fewer options; keep keyboard focus inside the options. */
  private restoreOptionFocus(index: number) {
    const card = this.questionCard?.nativeElement;
    if (index < 0 || !card || card.contains(document.activeElement)) return;
    const options = card.querySelectorAll<HTMLElement>('.quiz-option');
    options[Math.min(index, options.length - 1)]?.focus({ preventScroll: true });
  }

  private stopCelebration() {
    clearTimeout(this.celebrateTimer);
    this.celebrateTimer = undefined;
    resetConfetti();
  }

  private bringQuestionIntoView() {
    const card = this.questionCard?.nativeElement;
    if (!card || typeof window === 'undefined') return;
    // Only scroll when the top of the question is out of view (typical on phones).
    const top = card.getBoundingClientRect().top;
    if (top < 80 || top > window.innerHeight * 0.5) {
      window.scrollTo({ top: window.scrollY + top - 96, behavior: 'smooth' });
    }
  }
}

function storedLanguage() {
  try {
    return localStorage.getItem('selectedLanguage') || 'am';
  } catch {
    return 'am';
  }
}
